import * as Yup from 'yup'

export const REFERRAL_DETAILS = 'REFERRAL_DETAILS'

// ── Page & Dialog Labels ───────────────────────────────────────────────────
export const REFERRAL_DETAILS_TITLE = 'Referral Details'
export const ADD_REFERRAL = 'Add Referral'
export const EDIT_REFERRAL = 'Edit Referral'
export const EDIT_REFERRAL_TOOLTIP = 'Edit Referral'
export const REFERRED_HOSPITALS_TITLE = 'Referred Hospitals'
/**
 * Dialog title shown when creating a referred hospital.
 */
export const CREATE_HOSPITAL = 'Create Referred Hospital'
/**
 * Dialog title shown when editing a referred hospital.
 */
export const EDIT_HOSPITAL = 'Edit Referred Hospital'
/**
 * Tooltip text for the edit action on a referred hospital row.
 */
export const EDIT_HOSPITAL_TOOLTIP = 'Edit Referred Hospital'
export const SEARCH_HOSPITALS_PLACEHOLDER = 'Search For Referred Hospitals'

// ── Button Labels ──────────────────────────────────────────────────────────
export const BTN_CANCEL = 'Cancel'
export const BTN_SUBMIT = 'Submit'
export const BTN_UPDATE = 'Update'
export const BTN_CREATE = 'Create'
export const BTN_BACK = 'Back'

// ── Form Labels & Routes ───────────────────────────────────────────────────
export const REFERRAL_DASHBOARD_ROUTE = '/referralDashboard'
export const REFERRAL_FORM_PATIENT_UHID_LABEL = 'Patient UHID'
export const REFERRAL_FORM_PATIENT_NAME_LABEL = 'Patient Name'
export const REFERRAL_FORM_SERVICE_LABEL = 'Service'
export const REFERRAL_FORM_DEPARTMENT_LABEL = 'Department'
export const REFERRAL_FORM_IS_SALES_PERSON_LABEL = 'Is Sales Person'
export const REFERRAL_FORM_REFERRAL_NAME_LABEL = 'Referral Name'
export const REFERRAL_FORM_REFERRED_HOSPITAL_LABEL = 'Referred Hospital'
export const REFERRAL_FORM_REFERRED_DOCTOR_LABEL = 'Referred Doctor'
export const REFERRAL_FORM_REFERRAL_DATE_LABEL = 'Referral Date'
export const REFERRAL_FORM_REASON_LABEL = 'Reason for Referral'
export const REFERRAL_FORM_FETCH_DEPARTMENTS_ERROR =
  'Error fetching departments:'

// ── Toast Messages ─────────────────────────────────────────────────────────
export const TOAST_REFERRAL_SUBMIT_SUCCESS = 'Referral submitted successfully!'
export const TOAST_REFERRAL_SUBMIT_FAIL =
  'Failed to submit referral. Please try again.'
export const TOAST_REFERRAL_UPDATE_SUCCESS = 'Referral updated successfully!'
export const TOAST_REFERRAL_UPDATE_FAIL =
  'Failed to update referral. Please try again.'
export const TOAST_HOSPITAL_CREATE_SUCCESS = 'Hospital created successfully!'
export const TOAST_HOSPITAL_UPDATE_SUCCESS = 'Hospital updated successfully!'
/**
 * Builds the hospital operation failure toast message.
 */
export const TOAST_HOSPITAL_FAIL = (action: string) =>
  `Failed to ${action} hospital. Please try again.`
export const TOAST_HOSPITAL_DUPLICATE =
  'A hospital with the same name already exists for this location.'
export const TOAST_PATIENT_NOT_FOUND =
  'No patient found with the provided UHID. Please check and try again.'
export const TOAST_PATIENT_FETCH_FAIL =
  'Failed to fetch patient details. Please check the UHID and try again.'
export const TOAST_EXPORT_REFERRAL_NO_DATA =
  'No referral details found for the selected filters'
export const TOAST_EXPORT_REFERRAL_SUCCESS =
  'Referral details exported successfully.'
export const TOAST_EXPORT_REFERRAL_FAILED =
  'Failed to export referral details. Please try again.'

// ── Unknown User Fallback ──────────────────────────────────────────────────
export const UNKNOWN_USER = 'Unknown User'

// ── API Paths (appended to REACT_APP_API_GATEWAY_URL) ─────────────────────
export const API_PATH_REFERRAL_LIST = 'api/api/rest/referral-details'
export const API_PATH_REFERRAL_FILTER =
  'api/api/rest/get-referrer-details-by-filter'
export const API_PATH_REFERRAL_CREATE = 'api/api/rest/referrer-details/create'
export const API_PATH_REFERRAL_UPLOAD = 'api/api/rest/referrer-details/upload'
export const REFERRAL_DETAILS_UPLOAD = '/api/export-referrals/upload'
/**
 * Builds the referral update API path for a given referral ID.
 */
export const API_PATH_REFERRAL_UPDATE = (id: string | number) =>
  `api/api/rest/referral-details/update/${id}`
export const API_DUPLICATE_PATIENT_CHECK = 'api/api/rest/checkhospitalduplicate'
export const API_PATH_DEPARTMENTS = 'api/api/rest/departments'
export const API_PATH_HOSPITAL_LIST = 'api/api/rest/referred-hospital-list'
export const GET_HOSPITAL_LIST = 'api/api/rest/referred-hospital-list-all'
export const API_PATH_HOSPITAL_CREATE =
  'api/api/rest/referred-hospital-list/create'
/**
 * Builds the referred-hospital update API path for a given hospital ID.
 */
export const API_PATH_HOSPITAL_UPDATE = (id: string | number) =>
  `api/api/rest/referred-hospital-list/update/${id}`
export const API_PATH_HOSPITAL_CHECK_DUPLICATE =
  'api/api/rest/referred-hospital-list/check-duplicate'

// ── Referral Service Constants ────────────────────────────────────────────
export const FILTER_KEY_REFERRAL_DATE = 'referral_date'
export const FILTER_KEY_CREATED_ON = 'created_on'
export const FILTER_KEY_UPDATED_ON = 'updated_on'
export const DATE_TIME_SEPARATOR = 'T'
export const SXP_GET_PATIENTS_API = 'getPatientsAPI'
export const CONTACT_QUERY_PHONE = 'phone'
export const ERROR_FETCHING_HOSPITAL_LIST = 'Error fetching hospital list:'
export const ERROR_FETCHING_DOCTORS = 'Error fetching doctors:'

// ── Dropdown Options ───────────────────────────────────────────────────────
export const SERVICE_OPTIONS = [
  'OPD',
  'IPD',
  'Laboratory',
  'Radiology',
  'Pharmacy',
  'Physiotherapy',
]

export const REFERRED_DOCTOR_OPTIONS = [
  'Dr. Anil Kumar',
  'Dr. Priya Sharma',
  'Dr. Ramesh Babu',
  'Dr. Sunita Rao',
  'Dr. Vikram Nair',
  'Dr. Meena Iyer',
  'Dr. Suresh Patel',
  'Dr. Anjali Singh',
]

export const HOSPITAL_TYPE_OPTIONS = ['Hospital', 'Clinic', 'A&E']

// ── Referred Hospital Field Rules ─────────────────────────────────────────
export const HOSPITAL_FIELD_PHONE = 'phone'
export const HOSPITAL_FIELD_PINCODE = 'pincode'
export const HOSPITAL_FIELD_LOCATION = 'location'
export const NUMERIC_ONLY_SANITIZE_REGEX = /\D/g
export const NUMERIC_INPUT_PATTERN = '[0-9]*'
export const PHONE_INPUT_MAX_LENGTH = 10
export const PINCODE_INPUT_MAX_LENGTH = 7
export const PHONE_VALIDATION_REGEX = /^[6-9]\d{9}$/
export const PINCODE_VALIDATION_REGEX = /^\d{6,7}$/
export const PHONE_VALIDATION_ERROR = 'Enter a valid 10-digit phone number'
export const PINCODE_VALIDATION_ERROR = 'Enter a valid 6 to 7-digit pincode'

export const REFERRAL_SEARCH_OPTIONS = [
  { label: 'MUHID', value: 'patient_uhid' },
  { label: 'Name', value: 'patient_name' },
  { label: 'Mobile', value: 'patient_mobile' },
  { label: 'Hospital', value: 'hospital' },
  { label: 'Referring Doctor', value: 'referred_from' },
  { label: 'Referred Doctor', value: 'referred_to' },
  { label: 'Department', value: 'department' },
  { label: 'Service', value: 'service' },
  { label: 'Referred Date', value: 'referral_date' },
]

// ── Referral Export Dialog ────────────────────────────────────────────────
export const EXPORT_REFERRAL_DIALOG_TITLE = 'Export Referral Details'
export const EXPORT_PRESET_ONE_MONTH_LABEL = '1 Month'
export const EXPORT_PRESET_THREE_MONTHS_LABEL = '3 Months'
export const EXPORT_BETWEEN_DATES_LABEL = 'Between Dates'
export const EXPORT_GREATER_THAN_OR_EQUAL_LABEL = 'Greater Than or Equal To'
export const EXPORT_DATE_LABEL = 'Date'
export const EXPORT_START_DATE_LABEL = 'Start Date'
export const EXPORT_END_DATE_LABEL = 'End Date'
export const EXPORT_LOCATION_OPTIONAL_LABEL = 'Location (Optional)'
export const EXPORT_BUTTON_LABEL = 'Export'
export const EXPORTING_BUTTON_LABEL = 'Exporting...'
export const EXPORT_FILTER_MODE_BETWEEN_DATES = 'betweenDates'
export const EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual'
export const EXPORT_ONE_MONTH_DAYS = 30
export const EXPORT_THREE_MONTHS_DAYS = 90
export const EXPORT_MAX_RANGE_DAYS = 90
export const EXPORT_MS_PER_DAY = 1000 * 60 * 60 * 24
export const EXPORT_START_DATE_REQUIRED = 'Start date is required'
export const EXPORT_START_DATE_IN_FUTURE =
  'Start date cannot be greater than current date'
export const EXPORT_END_DATE_REQUIRED = 'End date is required'
export const EXPORT_END_DATE_IN_FUTURE =
  'End date cannot be greater than current date'
export const EXPORT_END_DATE_BEFORE_START =
  'End date cannot be earlier than start date'
export const EXPORT_DATE_RANGE_EXCEEDED =
  'Date range should not be more than 90 days'

// ── AG Grid Shared Helpers ─────────────────────────────────────────────────
export const AG_FILTER_PARAMS = { buttons: ['clear'], closeOnApply: true }

export const CELL_STYLE_CENTER = {
  fontSize: '12px',
  textAlign: 'center' as const,
}
export const CELL_STYLE_DEFAULT = { fontSize: '12px' }
export const CELL_STYLE_PROVENANCE = {
  fontSize: '12px',
  textAlign: 'left' as const,
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  lineHeight: 1.2,
  display: 'flex',
  alignItems: 'center',
}
export const CELL_STYLE_NAME = {
  fontSize: '12px',
  textAlign: 'left' as const,
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  lineHeight: 1.2,
  display: 'flex',
  alignItems: 'center',
}
export const CELL_STYLE_ACTION = {
  fontSize: '12px',
  textAlign: 'center' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
export const CELL_STYLE_WRAP = {
  fontSize: '12px',
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  whiteSpace: 'normal' as const,
  lineHeight: 1.2,
  display: 'flex',
  alignItems: 'center',
}
export interface HospitalForm {
  name: string
  description: string
  location: string
  street: string
  city: string
  state: string
  pincode: string
  phone: string
  email: string
  type: string
}

export interface FieldConfig {
  name: keyof HospitalForm
  label: string
  multiline?: boolean
  minRows?: number
  isAutocomplete?: boolean
  options?: string[]
  required?: boolean
}
export const FIELD_ROWS: (FieldConfig | FieldConfig[])[] = [
  { name: 'name', label: 'Name', required: true },
  { name: 'location', label: 'Location', required: true },
  {
    name: 'type',
    label: 'Type',
    isAutocomplete: true,
    options: HOSPITAL_TYPE_OPTIONS,
    required: true,
  },
  { name: 'street', label: 'Street' },
  [
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
  ],
  [
    { name: 'pincode', label: 'Pincode' },
    { name: 'phone', label: 'Phone' },
  ],
  { name: 'email', label: 'Email' },
  { name: 'description', label: 'Description', multiline: true, minRows: 3 },
]

export const hospitalValidationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  location: Yup.string().trim().required('Location is required'),
  type: Yup.string().required('Type is required'),
  email: Yup.string().trim().email('Enter a valid email address').optional(),
  pincode: Yup.string()
    .trim()
    .matches(PINCODE_VALIDATION_REGEX, PINCODE_VALIDATION_ERROR)
    .optional(),
  phone: Yup.string()
    .trim()
    .matches(PHONE_VALIDATION_REGEX, PHONE_VALIDATION_ERROR)
    .optional(),
})

export const referralValidationSchema = Yup.object({
  patientUhid: Yup.string().trim().required('Patient UHID is required'),
  service: Yup.array().min(1, 'Service is required'),
  department: Yup.string().required('Department is required'),
  referredHospital: Yup.string().required('Hospital is required'),
  referralDate: Yup.string()
    .required('Referral Date is required')
    .test('valid-date', 'Referral Date is invalid', (val) =>
      val ? !Number.isNaN(new Date(val).getTime()) : false
    ),
  reason: Yup.string().trim().required('Reason for Referral is required'),
})
export const PATIENT_MOBILE = 'patient_mobile'
export const PATIENT_NAME = 'patient_name'
export const MOBILE = 'mobile'

// ── ReferralDetails Column Headers ───────────────────────────────────────────
export const COL_HEADER_SL_NO = 'SL No'
export const COL_HEADER_UHID = 'UHID'
export const COL_HEADER_NAME = 'Name'
export const COL_HEADER_MOBILE_NO = 'Mobile No'
export const COL_HEADER_SERVICE = 'Service'
export const COL_HEADER_DEPARTMENT = 'Department'
export const COL_HEADER_SOURCE = 'Source'
export const COL_HEADER_REFERRED_HOSPITAL = 'Referred Hospital'
export const COL_HEADER_REFERRER_NAME = 'Referrer Name'
export const COL_HEADER_REFERRED_DOCTOR = 'Referred Doctor'
export const COL_HEADER_REASON = 'Reason For Referral'
export const COL_HEADER_REFERRAL_DATE = 'Referral Date'
export const COL_HEADER_CREATED_BY = 'Created By'
export const COL_HEADER_UPDATED_BY = 'Updated By'
export const COL_HEADER_ACTION = 'Action'

// ── ReferralDetails Button Labels ────────────────────────────────────────────
export const BTN_UPLOAD = 'Upload'

// ── ReferralDetails Toast / Error Messages ───────────────────────────────────
export const TOAST_UPLOAD_INVALID_FILE =
  'Please select an Excel file (.xlsx or .xls).'
export const TOAST_UPLOAD_SUCCESS_FALLBACK =
  'Referral upload completed successfully.'
export const TOAST_UPLOAD_FAILED = 'Referral upload failed. Please try again.'
export const ERROR_UPLOAD_FAILED = 'Referral upload failed:'

// ── Provenance Mode Labels ────────────────────────────────────────────────────
export const PROVENANCE_MODE_CREATE = 'CREATE'
export const PROVENANCE_MODE_UPDATE = 'UPDATE'
export const PROVENANCE_UNKNOWN = 'Unknown'
